import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Upload, Film, Image as ImageIcon, Mail } from "lucide-react";
import {
    API,
    fileUrl,
    getAdminToken,
    setAdminToken,
    adminHeaders,
} from "@/lib/api";

export default function Admin() {
    const [token, setToken] = useState(getAdminToken());

    if (!token) {
        return <Login onSuccess={(t) => { setAdminToken(t); setToken(t); }} />;
    }
    return (
        <Dashboard
            onLogout={() => {
                setAdminToken("");
                setToken("");
            }}
        />
    );
}

function Login({ onSuccess }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await axios.post(`${API}/admin/login`, { password });
            onSuccess(r.data.token);
            toast.success("Connexion réussie");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mot de passe incorrect");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="min-h-screen bg-bordeaux text-white flex items-center justify-center px-6"
            data-testid="admin-login-page"
        >
            <form
                onSubmit={submit}
                className="w-full max-w-sm"
                data-testid="admin-login-form"
            >
                <div className="flex flex-col items-center mb-8">
                    <img
                        src="/logo.png"
                        alt="Logo Mélanie Perez"
                        className="w-28 h-28 object-contain mb-4"
                    />
                    <p className="font-display text-3xl text-white">
                        Mélanie <span className="text-fuchsia">Perez</span>
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-gold mt-1">
                        Créatrice de contenu &amp; UGC
                    </p>
                </div>
                <p className="eyebrow !text-gold text-center mb-2">Administration</p>
                <p className="text-white/50 text-sm text-center mt-2 mb-10">
                    Espace privé — gestion du contenu
                </p>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">
                    Mot de passe
                </label>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password-input"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-gold focus:outline-none py-3 text-white"
                    autoFocus
                />
                <button
                    type="submit"
                    disabled={loading || !password}
                    data-testid="admin-login-submit"
                    className="mt-8 w-full bg-white text-ink py-4 text-xs uppercase tracking-[0.22em] hover:bg-fuchsia hover:text-white transition-colors disabled:opacity-60"
                >
                    {loading ? "…" : "Se connecter"}
                </button>
                <p className="text-white/30 text-xs text-center mt-8">
                    <a href="/" className="hover:text-white transition-colors">
                        ← Retour au site
                    </a>
                </p>
            </form>
        </main>
    );
}

function Dashboard({ onLogout }) {
    const [tab, setTab] = useState("videos");
    return (
        <main className="min-h-screen bg-white text-ink" data-testid="admin-dashboard">
            <header className="border-b border-[#E5E5E5] px-6 md:px-12 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <img
                        src="/logo.png"
                        alt="Logo Mélanie Perez"
                        className="w-10 h-10 object-contain"
                    />
                    <p className="font-display text-xl md:text-2xl leading-tight">
                        Mélanie <span className="text-fuchsia">Perez</span>{" "}
                        <span className="text-[#999] text-xs font-body uppercase tracking-[0.28em] ml-2">
                            admin
                        </span>
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <a
                        href="/"
                        className="text-xs uppercase tracking-[0.22em] hover:text-fuchsia"
                    >
                        Voir le site
                    </a>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] hover:text-fuchsia"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Déconnexion
                    </button>
                </div>
            </header>

            <nav className="border-b border-[#E5E5E5] px-6 md:px-12 flex gap-8 overflow-x-auto no-scrollbar">
                {[
                    { id: "videos", label: "Vidéos UGC", icon: Film },
                    { id: "logos", label: "Logos marques", icon: ImageIcon },
                    { id: "briefs", label: "Briefs reçus", icon: Mail },
                ].map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`admin-tab-${t.id}`}
                        className={`py-5 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 border-b-2 transition-colors ${
                            tab === t.id
                                ? "border-fuchsia text-ink"
                                : "border-transparent text-[#999] hover:text-ink"
                        }`}
                    >
                        <t.icon className="w-3.5 h-3.5" />
                        {t.label}
                    </button>
                ))}
            </nav>

            <div className="px-6 md:px-12 py-10 max-w-6xl">
                {tab === "videos" && <VideosTab />}
                {tab === "logos" && <LogosTab />}
                {tab === "briefs" && <BriefsTab />}
            </div>
        </main>
    );
}

function VideosTab() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", category: "UGC", order: 0 });
    const [video, setVideo] = useState(null);
    const [poster, setPoster] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/videos`);
        setItems(r.data || []);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!video) return toast.error("Sélectionnez un fichier vidéo");
        if (!form.title.trim()) return toast.error("Donnez un titre à la vidéo");
        setUploading(true);
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("order", String(form.order || 0));
        fd.append("video", video);
        if (poster) fd.append("poster", poster);
        try {
            await axios.post(`${API}/admin/videos`, fd, {
                headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
            });
            toast.success("Vidéo ajoutée");
            setForm({ title: "", category: "UGC", order: 0 });
            setVideo(null);
            setPoster(null);
            // reset file inputs
            document.getElementById("video-input").value = "";
            const pi = document.getElementById("poster-input");
            if (pi) pi.value = "";
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'upload");
        } finally {
            setUploading(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette vidéo ?")) return;
        try {
            await axios.delete(`${API}/admin/videos/${id}`, {
                headers: adminHeaders(),
            });
            toast.success("Supprimée");
            load();
        } catch (err) {
            toast.error("Erreur");
        }
    };

    return (
        <div className="grid lg:grid-cols-12 gap-10">
            <form
                onSubmit={submit}
                className="lg:col-span-5 border border-[#E5E5E5] p-6 space-y-5"
                data-testid="video-upload-form"
            >
                <h2 className="font-display text-2xl">Ajouter une vidéo</h2>
                <Field
                    label="Titre"
                    value={form.title}
                    onChange={(v) => setForm({ ...form, title: v })}
                    testId="video-title"
                />
                <Field
                    label="Catégorie"
                    value={form.category}
                    onChange={(v) => setForm({ ...form, category: v })}
                    testId="video-category"
                />
                <Field
                    label="Ordre (0 = en premier)"
                    type="number"
                    value={form.order}
                    onChange={(v) => setForm({ ...form, order: Number(v) || 0 })}
                    testId="video-order"
                />
                <FileField
                    id="video-input"
                    label="Fichier vidéo (mp4, mov…)"
                    accept="video/*"
                    file={video}
                    onChange={setVideo}
                    testId="video-file"
                />
                <FileField
                    id="poster-input"
                    label="Image de couverture (optionnel)"
                    accept="image/*"
                    file={poster}
                    onChange={setPoster}
                    testId="video-poster"
                />
                <button
                    type="submit"
                    disabled={uploading}
                    data-testid="video-submit"
                    className="w-full bg-bordeaux text-white py-3 text-xs uppercase tracking-[0.22em] hover:bg-fuchsia disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                    <Upload className="w-4 h-4" />
                    {uploading ? "Upload…" : "Publier la vidéo"}
                </button>
            </form>

            <div className="lg:col-span-7">
                <h2 className="font-display text-2xl mb-6">
                    Vidéos publiées ({items.length})
                </h2>
                {items.length === 0 ? (
                    <p className="text-[#999] text-sm">
                        Aucune vidéo pour le moment. Les placeholders sont affichés en attendant.
                    </p>
                ) : (
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {items.map((v) => (
                            <li
                                key={v.id}
                                className="relative aspect-[9/16] bg-bordeaux overflow-hidden group"
                                data-testid={`admin-video-${v.id}`}
                            >
                                {v.poster_url ? (
                                    <img
                                        src={fileUrl(v.poster_url)}
                                        alt={v.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ) : (
                                    <video
                                        src={fileUrl(v.video_url)}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        muted
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                <div className="absolute bottom-2 left-2 right-2 text-white">
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold">
                                        {v.category}
                                    </p>
                                    <p className="font-display text-sm truncate">{v.title}</p>
                                </div>
                                <button
                                    onClick={() => del(v.id)}
                                    data-testid={`admin-video-delete-${v.id}`}
                                    className="absolute top-2 right-2 w-8 h-8 bg-white/90 text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-fuchsia hover:text-white transition-all"
                                    aria-label="Supprimer"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function LogosTab() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ name: "", order: 0 });
    const [image, setImage] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/logos`);
        setItems(r.data || []);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!image) return toast.error("Sélectionnez un logo");
        if (!form.name.trim()) return toast.error("Nom de la marque ?");
        setUploading(true);
        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("order", String(form.order || 0));
        fd.append("image", image);
        try {
            await axios.post(`${API}/admin/logos`, fd, {
                headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
            });
            toast.success("Logo ajouté");
            setForm({ name: "", order: 0 });
            setImage(null);
            document.getElementById("logo-input").value = "";
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'upload");
        } finally {
            setUploading(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ce logo ?")) return;
        await axios.delete(`${API}/admin/logos/${id}`, { headers: adminHeaders() });
        toast.success("Supprimé");
        load();
    };

    return (
        <div className="grid lg:grid-cols-12 gap-10">
            <form
                onSubmit={submit}
                className="lg:col-span-5 border border-[#E5E5E5] p-6 space-y-5"
                data-testid="logo-upload-form"
            >
                <h2 className="font-display text-2xl">Ajouter un logo</h2>
                <Field
                    label="Nom de la marque"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    testId="logo-name"
                />
                <Field
                    label="Ordre"
                    type="number"
                    value={form.order}
                    onChange={(v) => setForm({ ...form, order: Number(v) || 0 })}
                    testId="logo-order"
                />
                <FileField
                    id="logo-input"
                    label="Image (png/svg)"
                    accept="image/*"
                    file={image}
                    onChange={setImage}
                    testId="logo-file"
                />
                <button
                    type="submit"
                    disabled={uploading}
                    data-testid="logo-submit"
                    className="w-full bg-bordeaux text-white py-3 text-xs uppercase tracking-[0.22em] hover:bg-fuchsia disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                    <Upload className="w-4 h-4" /> {uploading ? "Upload…" : "Publier le logo"}
                </button>
            </form>
            <div className="lg:col-span-7">
                <h2 className="font-display text-2xl mb-6">
                    Logos publiés ({items.length})
                </h2>
                {items.length === 0 ? (
                    <p className="text-[#999] text-sm">
                        Aucun logo. Les noms de marques par défaut sont affichés.
                    </p>
                ) : (
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {items.map((l) => (
                            <li
                                key={l.id}
                                className="relative aspect-square bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center group p-6"
                                data-testid={`admin-logo-${l.id}`}
                            >
                                <img
                                    src={fileUrl(l.image_url)}
                                    alt={l.name}
                                    className="max-h-20 max-w-full object-contain"
                                />
                                <p className="absolute bottom-1 left-2 right-2 text-[10px] uppercase tracking-[0.2em] text-[#999] truncate">
                                    {l.name}
                                </p>
                                <button
                                    onClick={() => del(l.id)}
                                    data-testid={`admin-logo-delete-${l.id}`}
                                    className="absolute top-2 right-2 w-8 h-8 bg-white text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-fuchsia hover:text-white border border-[#E5E5E5] transition-all"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function BriefsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axios
            .get(`${API}/contact`, { headers: adminHeaders() })
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Erreur de chargement"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div>
            <h2 className="font-display text-2xl mb-6">Briefs reçus ({items.length})</h2>
            {loading ? (
                <p className="text-[#999] text-sm">Chargement…</p>
            ) : items.length === 0 ? (
                <p className="text-[#999] text-sm">Aucun brief pour le moment.</p>
            ) : (
                <ul className="space-y-4" data-testid="briefs-list">
                    {items.map((b) => (
                        <li
                            key={b.id}
                            className="border border-[#E5E5E5] p-6 hover:border-gold transition-colors"
                            data-testid={`brief-${b.id}`}
                        >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <h3 className="font-display text-xl">
                                    {b.name}
                                    {b.brand && (
                                        <span className="text-[#999] font-body text-sm ml-3">
                                            · {b.brand}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-xs text-[#999]">
                                    {new Date(b.timestamp).toLocaleString("fr-FR")}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs text-[#666]">
                                <a
                                    href={`mailto:${b.email}`}
                                    className="text-fuchsia hover:underline"
                                >
                                    {b.email}
                                </a>
                                {b.instagram && <span>{b.instagram}</span>}
                                {b.project_type && (
                                    <span className="uppercase tracking-[0.18em]">
                                        {b.project_type}
                                    </span>
                                )}
                                {b.budget && (
                                    <span className="uppercase tracking-[0.18em] text-gold">
                                        {b.budget}
                                    </span>
                                )}
                            </div>
                            <p className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">
                                {b.message}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#666] mb-1.5">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full border-b border-[#E5E5E5] focus:border-ink focus:outline-none py-2 bg-transparent"
            />
        </div>
    );
}

function FileField({ id, label, accept, file, onChange, testId }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#666] mb-1.5">
                {label}
            </label>
            <input
                id={id}
                type="file"
                accept={accept}
                onChange={(e) => onChange(e.target.files?.[0] || null)}
                data-testid={testId}
                className="block w-full text-sm text-[#666] file:mr-4 file:py-2 file:px-4 file:border file:border-[#E5E5E5] file:bg-white file:text-ink file:text-xs file:uppercase file:tracking-[0.18em] hover:file:bg-bordeaux hover:file:text-white file:cursor-pointer file:transition-colors"
            />
            {file && (
                <p className="text-xs text-[#999] mt-1 truncate">
                    {file.name} ({Math.round(file.size / 1024)} Ko)
                </p>
            )}
        </div>
    );
}
